import { useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Send, Mail, MessageSquare, User, Loader2, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t('Veuillez remplir tous les champs', 'Bitte alle Felder ausfüllen'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success(t('Message envoyé avec succès !', 'Nachricht erfolgreich gesendet!'));
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('Erreur lors de l\'envoi du message', 'Fehler beim Senden der Nachricht'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-slate-100 to-slate-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block animate-fadeInUp">
            {t('Contact', 'Kontakt')}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6 animate-fadeInUp delay-100 font-outfit">
            {t('Échangeons !', 'Lass uns reden!')}
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed animate-fadeInUp delay-200 max-w-2xl mx-auto">
            {t(
              "Une question ? Besoin d'un conseil ? Envie d'échanger ou de partager ton expérience ? Je ferai de mon mieux pour répondre.",
              "Eine Frage? Brauchst du einen Rat? Möchtest du dich austauschen oder deine Erfahrung teilen? Ich werde mein Bestes tun, um zu antworten."
            )}
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          {submitted ? (
            <div className="text-center py-16 bg-green-50 rounded-3xl border border-green-200 animate-scaleIn">
              <CheckCircle className="w-20 h-20 mx-auto mb-6 text-green-500" />
              <h2 className="text-2xl font-bold text-slate-900 mb-4 font-outfit">
                {t('Message envoyé !', 'Nachricht gesendet!')}
              </h2>
              <p className="text-slate-600 mb-8">
                {t(
                  "Merci pour ton message. Je te répondrai dès que possible.",
                  "Danke für deine Nachricht. Ich werde dir so schnell wie möglich antworten."
                )}
              </p>
              <Button 
                onClick={() => setSubmitted(false)}
                className="btn-primary rounded-full"
                data-testid="send-another-message"
              >
                {t('Envoyer un autre message', 'Eine weitere Nachricht senden')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2 text-slate-700 font-medium">
                  <User className="w-4 h-4" />
                  {t('Ton nom', 'Dein Name')}
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('Comment tu t\'appelles ?', 'Wie heißt du?')}
                  className="h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white"
                  data-testid="contact-name-input"
                  required
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2 text-slate-700 font-medium">
                  <Mail className="w-4 h-4" />
                  {t('Ton email', 'Deine E-Mail')}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('Pour que je puisse te répondre', 'Damit ich dir antworten kann')}
                  className="h-14 rounded-xl border-slate-200 bg-slate-50 focus:bg-white"
                  data-testid="contact-email-input"
                  required
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="flex items-center gap-2 text-slate-700 font-medium">
                  <MessageSquare className="w-4 h-4" />
                  {t('Ton message', 'Deine Nachricht')}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('Dis-moi tout...', 'Erzähl mir alles...')}
                  rows={6}
                  className="rounded-xl border-slate-200 bg-slate-50 focus:bg-white resize-none"
                  data-testid="contact-message-input"
                  required
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-full btn-primary rounded-full py-6 text-base font-bold"
                data-testid="contact-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    {t('Envoi en cours...', 'Wird gesendet...')}
                  </>
                ) : (
                  <>
                    <Send className="mr-2 w-5 h-5" />
                    {t('Envoyer le message', 'Nachricht senden')}
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 font-outfit">
            {t('Pourquoi me contacter ?', 'Warum mich kontaktieren?')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: t('Une question', 'Eine Frage'),
                description: t('Sur mon parcours ou sur l\'Allemagne', 'Über meinen Weg oder über Deutschland'),
                icon: '❓'
              },
              {
                title: t('Un conseil', 'Ein Rat'),
                description: t('Pour préparer ton propre voyage', 'Um deine eigene Reise vorzubereiten'),
                icon: '💡'
              },
              {
                title: t('Un échange', 'Ein Austausch'),
                description: t('Partager ton expérience avec moi', 'Deine Erfahrung mit mir teilen'),
                icon: '🤝'
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-2xl p-6 card-hover"
                data-testid={`contact-reason-${index}`}
              >
                <span className="text-4xl mb-4 block">{item.icon}</span>
                <h3 className="text-lg font-bold text-slate-900 mb-2 font-outfit">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
